import { utc } from 'rewire-common';
import { GQL } from 'rewire-graphql';
import {
  IMessage,
  ISubscriptionRecord,
  MessageFilterInput
} from '../DataTypes';
import DataQueries from '../graphql/Queries';
import gql from 'graphql-tag';
import PagingModel from '../settings/PagingModel';
import EntitySubscriptionService, {
  EntitySubscriptionModel,
  SubscriptionCallbackFn,
} from './EntitySubscriptionService';
import { subscribe } from '../graphql/GraphQLClient';

const messagesSubscription = (returnSchema: string) => gql`
  subscription($filter: MessageFilterInput!) {
    messages(filter: $filter) {
      message {
        ${returnSchema}
      }
      operation
    }
  }
`;

export function subscribeToMessages(returnSchema: string, filter: MessageFilterInput, callback: any): any {
  return subscribe(messagesSubscription(returnSchema) as GQL, { filter }, callback, 'messages');
}

export type MessageSort = (m1: IMessage, m2: IMessage) => number;

export function defaultMessageSort(m1: IMessage, m2: IMessage) {
  const m1Priority = getMessageLevelPriority(m1);
  const m2Priority = getMessageLevelPriority(m2);

  if (m1Priority < m2Priority) {
    return -1;
  }

  if (m1Priority > m2Priority) {
    return 1;
  }

  return m1.id.localeCompare(m2.id);
}

export function getMessageLevelPriority(message: IMessage) {
  switch (message.level) {
    case 'error':
      return 0;
    case 'warning':
      return 1;
    case 'info':
      return 2;
    case 'success':
      return 3;
    default:
      return 4;
  }
}

export const defaultMessagesReturnSchema = `
  id
  text
  level
  type
  effective
`;

export class MessagesSubscriptionModel extends EntitySubscriptionModel<IMessage> {
  constructor(options?: { returnSchema?: string, filter?: MessageFilterInput, sort?: MessageSort, pagingModel?: PagingModel, externalEntitiesContainer?: IMessage[], subscriptionCallback?: SubscriptionCallbackFn<IMessage> }) {
    super({ returnSchema: options?.returnSchema || defaultMessagesReturnSchema, filter: options?.filter as Record<string, unknown> | undefined, sort: options?.sort || defaultMessageSort, pagingModel: options?.pagingModel, externalEntitiesContainer: options?.externalEntitiesContainer, subscriptionCallback: options?.subscriptionCallback });
  }

  set messages(value: IMessage[]) {
    this.entities = value;
  }

  get messages(): IMessage[] {
    return this.entities;
  }

  get errorMessages(): IMessage[] {
    return this.messages.filter(m => m.level === 'error');
  }

  get warningMessages(): IMessage[] {
    return this.messages.filter(m => m.level === 'warning');
  }

  get infoMessages(): IMessage[] {
    return this.messages.filter(m => m.level === 'info');
  }

  get successMessages(): IMessage[] {
    return this.messages.filter(m => m.level === 'success');
  }

  get messageCount(): number {
    return this.entityCount;
  }

  get errorMessagesCount(): number {
    return this.errorMessages.length;
  }

  get warningMessagesCount(): number {
    return this.warningMessages.length;
  }

  get infoMessagesCount(): number {
    return this.infoMessages.length;
  }

  get successMessagesCount(): number {
    return this.successMessages.length;
  }

  protected async _getEntities(returnSchema: string, filter?: Record<string, unknown>): Promise<IMessage[]> {
    const messages = [];
    let result = undefined;
    let cursor = undefined;
    do {
      result = await DataQueries.getMessagesSearch(returnSchema, filter, 200, cursor);
      cursor = result?.cursor;
      messages.push(...result?.data as IMessage[]);
    } while (cursor);
    return messages;
  }

  protected subscribe(returnSchema: string, filter: Record<string, unknown>, callback: any): any {
    return subscribeToMessages(returnSchema, filter, callback);
  }

  protected mergeAccessor(messagesRecord: ISubscriptionRecord<IMessage>): IMessage {
    return messagesRecord['message'] as IMessage;
  }

  protected async merge(messageRecord: ISubscriptionRecord<IMessage>) {
    const message = this.mergeAccessor(messageRecord);
    message.effective = utc((message.effective as any).value); // Message sent by subscription has malformed UTC object.
    await super.merge(messageRecord);
  }
}

class MessagesSubscriptionService extends EntitySubscriptionService<IMessage, MessagesSubscriptionModel> {
  constructor() {
    super();
  }

  protected _createSubscriptionModel(options?: { returnSchema?: string, filter?: MessageFilterInput, sort?: MessageSort, pagingModel?: PagingModel, externalEntitiesContainer?: IMessage[], subscriptionCallback?: SubscriptionCallbackFn<IMessage> }): MessagesSubscriptionModel {
    return new MessagesSubscriptionModel(options);
  }
}

const messagesSubscriptionService = new MessagesSubscriptionService();
export default messagesSubscriptionService;
